import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {volumeUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import {Pagination} from 'lib/Listings';
import {FillPlateModalReducerActionType} from 'samples/plate-maps/fill-plate-modal/machine/FillPlateModalConstants';
import {PlateModalMachineAction} from 'samples/plate-maps/fill-plate-modal/PlateModalMachineProvider';
import {
  PlateMap,
  PlateMaterialInput,
  PlateModalAnalyticsMetadata,
  ReagentAssignmentGroup,
} from 'samples/plate-maps/PlateMapConstants';
import {FillPlateTransferTableViewMode} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {MeasurementWithMixedValuesInput} from 'samples/plate-maps/transfer-table/MeasurementWithMixedValuesInput';
import SummaryViewTransferTable from 'samples/plate-maps/transfer-table/SummaryViewTransferTable';
import {rollUpTotalWellQuantity} from 'samples/plate-maps/transfer-table/SummaryViewTransferTableUtil';
import WellViewTransferTable from 'samples/plate-maps/transfer-table/WellViewTransferTable';
import {FONT_PROXIMA_16} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY, GRAY_DARKER, GRAY_LIGHTER, GRAY_SEMIDARK} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import ToggleGroup from 'ui-library/ToggleGroup';

export interface FillPlateTransferTableProps {
  /** A function to dispatch an update to the transfer table state. */
  sendPlateModalMachineAction: PlateModalMachineAction;
  /** Information about how to page through the selected wells. */
  pagination: Pagination;
  /** The plate map being filled. */
  plateMap: PlateMap;
  /** In a procedure context, a list of plate-used material inputs. */
  plateMaterialInputs?: Array<PlateMaterialInput>;
  /** The set of reagent schemas in the plate. */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Whether the table is looking at the plate as a whole (i.e. the reagent schemas) or each well in the plate */
  viewMode: FillPlateTransferTableViewMode;
  /** Whether to show the empty state because all wells on the plate are empty */
  isEmpty: boolean;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
  /** Optional ref to the help text div */
  helpTextRef?: React.RefObject<HTMLDivElement>;
}

export default function FillPlateTransferTable({
  sendPlateModalMachineAction,
  pagination,
  plateMap,
  plateMaterialInputs,
  reagentGroupsById,
  selectedWellIndices,
  viewMode,
  isEmpty,
  analyticsMetadata,
  helpTextRef,
}: FillPlateTransferTableProps): JSX.Element {
  const {measurement, errors} = rollUpTotalWellQuantity(plateMap, selectedWellIndices);
  return (
    <div>
      <div className={css(styles.wellSelectionHelpText)} ref={helpTextRef}>
        In well view, select wells in the plate map to filter and configure transfer quantities in the table
        below
      </div>
      <div className={css(styles.header)}>
        {viewMode === FillPlateTransferTableViewMode.SUMMARY && (
          <MeasurementWithMixedValuesInput
            unitTypeFilter={volumeUnitTypePredicate}
            errors={errors}
            label="Total well volume"
            value={measurement.value}
            onChangeValue={(newValue) => {
              sendPlateModalMachineAction({
                type: FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_VALUE,
                totalWellQuantityValue: newValue,
              });
            }}
            unit={measurement.unit}
            onChangeUnit={(newUnit) => {
              sendPlateModalMachineAction({
                type: FillPlateModalReducerActionType.SET_TOTAL_WELL_QUANTITY_UNITS,
                totalWellQuantityUnit: newUnit as MeasurementUnit,
              });
            }}
          />
        )}
        {viewMode === FillPlateTransferTableViewMode.WELL && (
          <div className={css(styles.wellsSelectedText)}>{`${selectedWellIndices.size} of ${
            plateMap.size.numRows * plateMap.size.numColumns
          } wells selected`}</div>
        )}
        {!isEmpty && (
          <div className={css(styles.viewModeToggleWrapper)}>
            <FieldLabel label="Table view" />
            <ToggleGroup
              options={[
                {id: FillPlateTransferTableViewMode.WELL, buttonProps: {text: 'Well'}},
                {id: FillPlateTransferTableViewMode.SUMMARY, buttonProps: {text: 'Summary'}},
              ]}
              selection={viewMode}
              onChangeSelection={(newViewMode) => {
                analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.CHANGED_TRANSFER_TABLE_VIEW_MODE, {
                  ...analyticsMetadata,
                  viewMode: newViewMode,
                });
                sendPlateModalMachineAction({
                  type: FillPlateModalReducerActionType.SET_VIEW_MODE,
                  viewMode: newViewMode,
                });
              }}
              size={ButtonSize.SMALL}
              isFullWidth={true}
            />
          </div>
        )}
      </div>
      {isEmpty && (
        <div className={css(styles.emptyView)}>
          <p className={css(styles.emptyViewText)}>No contents added to this plate</p>
        </div>
      )}
      {viewMode === FillPlateTransferTableViewMode.SUMMARY && !isEmpty && (
        <SummaryViewTransferTable
          sendPlateModalMachineAction={sendPlateModalMachineAction}
          plateMap={plateMap}
          plateMaterialInputs={plateMaterialInputs}
          reagentGroupsById={reagentGroupsById}
          selectedWellIndices={selectedWellIndices}
          analyticsMetadata={analyticsMetadata}
        />
      )}
      {viewMode === FillPlateTransferTableViewMode.WELL && !isEmpty && (
        <WellViewTransferTable
          sendPlateModalMachineAction={sendPlateModalMachineAction}
          pagination={pagination}
          plateMap={plateMap}
          plateMaterialInputs={plateMaterialInputs}
          reagentGroupsById={reagentGroupsById}
          selectedWellIndices={selectedWellIndices}
          analyticsMetadata={analyticsMetadata}
          testOrderInfoById={plateMap.testOrderDilutionInfoById ?? {}}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  header: {
    border: `1px solid ${GRAY}`,
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Overlap the internal padding on the TableEditable
    marginBottom: '-11px',
    // Align with the right margin on the TableEditable
    marginRight: '10px',
    padding: '12px',
  },
  viewModeToggleWrapper: {
    // wide enough so that both buttons can be the same size
    width: '208px',
  },
  wellSelectionHelpText: {
    color: GRAY_DARKER,
    marginBottom: '12px',
  },
  wellsSelectedText: {
    color: GRAY_SEMIDARK,
    marginRight: '16px',
  },
  emptyView: {
    backgroundColor: GRAY_LIGHTER,
    height: '176px',
    marginRight: '10px',
    border: `1px solid ${GRAY}`,
    display: 'flex',
  },
  emptyViewText: {
    ...FONT_PROXIMA_16,
    color: GRAY_SEMIDARK,
    margin: 'auto',
  },
});
